import PropTypes from 'prop-types'
import React from 'react'
import { injectStripe } from 'react-stripe-elements'

import Icon from 'components/Icon'
import PaymentIcons from 'components/PaymentIcons'
import { LocalizationContext } from 'contexts/Localization'
import { MenuMainContext } from 'contexts/MenuMain'
import { ResizeContext } from 'contexts/Resize'
import { useCardInfoForm } from 'hooks/cart/useCardInfoForm'
import { TextLH } from 'styles/components/typography'
import { scrollToBlock } from 'utils/client/scroll'

import CreditCardBlock from './components/CreditCardBlock'
import PaymentRequestButton from './components/PaymentRequestButton'
import {
  ButtonBox,
  ButtonsWrapper,
  CartTextStyled,
  IconBox,
  StripeBlock,
  Wrapper,
  ButtonContent,
  ButtonAsyncActionStStyled,
  Indent,
  Ordering,
} from './styles'
import getCartOptions from './utils/getCartOptions'

const PAYMENT_ICONS = ['pci', 'norton', 'verify', 'secure']

const StripePaymentBlock = ({
  texts,
  cart,
  sales,
  stripe,

  handleSuccess,
  handleFailure,
  handleCollectWithChecks,
  handlePayment,
}) => {
  const { dict } = React.useContext(LocalizationContext)

  const { isOrderSucceed, isFetching, isOrderCollected } = cart
  const { menuNode } = React.useContext(MenuMainContext)
  const { mobile } = React.useContext(ResizeContext)
  const ccFormRef = React.useRef(null)

  const payBtnRef = React.useRef(null)

  /*
   * Card Info logic
   * > Card Info change
   */
  const [isCardReady, handleCardInfoCompleteChange] = useCardInfoForm()

  /*
   * Callback
   * > Payment handler
   */
  const handlePaymentWithCartCheck = React.useCallback(() => {
    if (!isCardReady) {
      scrollToBlock(ccFormRef, menuNode)
    }

    // If card info is not ready we should still send form
    // to mark the invalid fields (it happens under the stripe's hood)
    handlePayment({ stripe })

    return !!isCardReady
  }, [isCardReady, ccFormRef, handlePayment, stripe, menuNode])

  const [paymentRequest, setPaymentRequest] = React.useState(null)
  const [btnCanMakePayment, setBtnMakePayment] = React.useState(false)

  React.useEffect(() => {
    if (stripe && !sales.isGroupon) {
      const paymentRequest = stripe.paymentRequest(
        getCartOptions(cart, dict, texts, sales),
      )
      // console.log(getCartOptions(cart, dict, texts, sales), paymentRequest)
      setPaymentRequest(paymentRequest)
    }
  }, [stripe, cart, dict, texts, sales])

  React.useEffect(() => {
    paymentRequest?.canMakePayment().then(result => {
      setBtnMakePayment(result)
    })
  }, [paymentRequest])

  React.useEffect(() => {
    if (!isFetching && isOrderCollected && isOrderSucceed && sales.isGroupon)
      handleSuccess()
  }, [
    isOrderCollected,
    isOrderSucceed,
    sales.isGroupon,
    handleSuccess,
    isFetching,
    cart,
  ])

  return (
    <>
      <div ref={ccFormRef}>
        <CreditCardBlock
          btnCanMakePayment={!!btnCanMakePayment}
          onSubmit={handleCollectWithChecks}
          onCreditCardCompleteChange={handleCardInfoCompleteChange}
        />
      </div>

      <Ordering>
        <Wrapper>
          <CartTextStyled
            dangerouslySetInnerHTML={{
              __html: texts.form.payment.title,
            }}
          />
          {!mobile && <>&nbsp;</>}
          <StripeBlock>
            <Icon id="pay_stripe" />
          </StripeBlock>
        </Wrapper>

        <ButtonsWrapper>
          {btnCanMakePayment && (
            <>
              <ButtonBox>
                <PaymentRequestButton
                  ref={payBtnRef}
                  cart={cart}
                  texts={texts}
                  canMakePayment={btnCanMakePayment}
                  paymentRequest={paymentRequest}
                  handleSuccess={handleSuccess}
                />
              </ButtonBox>
              <Indent />
            </>
          )}
          <ButtonBox>
            <ButtonAsyncActionStStyled
              onClick={handlePaymentWithCartCheck}
              isLoading={isCardReady && isFetching}
              isSucceed={isCardReady && !!isOrderSucceed}
              successCb={isCardReady ? handleSuccess : null}
              failureCb={isCardReady ? handleFailure : null}
              animAlign="center"
            >
              <ButtonContent>
                <TextLH
                  dangerouslySetInnerHTML={{
                    __html: dict.buttons.place_order,
                  }}
                />
              </ButtonContent>
            </ButtonAsyncActionStStyled>
          </ButtonBox>
        </ButtonsWrapper>
      </Ordering>

      <IconBox>
        <PaymentIcons icons={PAYMENT_ICONS} />
      </IconBox>
    </>
  )
}

StripePaymentBlock.propTypes = {
  texts: PropTypes.object.isRequired,
  cart: PropTypes.object.isRequired,
  sales: PropTypes.object.isRequired,
  stripe: PropTypes.object,

  handleSuccess: PropTypes.func.isRequired,
  handleFailure: PropTypes.func.isRequired,
  handleCollectWithChecks: PropTypes.func.isRequired,
  handlePayment: PropTypes.func.isRequired,
}

export default injectStripe(StripePaymentBlock)
